import { useEffect, useRef, useState } from 'react';
import { voices, type CustomerVoice } from '@/mocks/voices';

type Variant = 'light' | 'gray' | 'dark';

interface Props {
  variant?: Variant;
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  items?: CustomerVoice[];
  showFootnote?: boolean;
  className?: string;
}

const accentColor: Record<CustomerVoice['accent'], string> = {
  blue: '#2563EB',
  orange: '#F97316',
  navy: '#0F172A',
};

function FadeIn({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className="transition-all duration-700"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(24px)',
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

export default function CustomerVoiceSection({
  variant = 'gray',
  eyebrow = 'Customer Voice',
  title = '来自真实参训团队的反馈',
  subtitle = '以下反馈来自完成培训的客户团队，已按角色匿名整理。',
  items = voices,
  showFootnote = true,
  className = '',
}: Props) {
  const bg = variant === 'dark' ? 'bg-[#0F172A]' : variant === 'gray' ? 'bg-[#F8FAFC]' : 'bg-white';
  const isDark = variant === 'dark';
  const titleColor = isDark ? 'text-white' : 'text-[#0F172A]';
  const subColor = isDark ? 'text-white/55' : 'text-[#64748B]';
  const eyebrowColor = isDark ? 'text-white/40' : 'text-[#64748B]';
  const cardBg = isDark
    ? 'bg-white/[0.04] border-white/10'
    : variant === 'gray'
    ? 'bg-white border-slate-100'
    : 'bg-[#F8FAFC] border-slate-100';
  const cardText = isDark ? 'text-white/75' : 'text-[#475569]';
  const roleColor = isDark ? 'text-white' : 'text-[#0F172A]';
  const teamColor = isDark ? 'text-white/45' : 'text-[#64748B]';

  return (
    <section className={`py-24 md:py-28 ${bg} ${className}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <FadeIn>
          <div className="mb-12 max-w-2xl">
            <span
              className={`text-xs uppercase tracking-widest ${eyebrowColor} mb-4 block`}
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {eyebrow}
            </span>
            <h2
              className={`text-3xl md:text-4xl font-bold ${titleColor} leading-tight mb-4`}
              style={{ fontFamily: '\'Noto Sans SC\', sans-serif', letterSpacing: '-0.02em' }}
            >
              {title}
            </h2>
            <p
              className={`text-base ${subColor} leading-relaxed`}
              style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
            >
              {subtitle}
            </p>
          </div>
        </FadeIn>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((v, idx) => {
            const color = accentColor[v.accent];
            return (
              <FadeIn key={v.id} delay={idx * 70}>
                <article
                  className={`rounded-2xl p-7 border h-full flex flex-col ${cardBg}`}
                >
                  <div className="flex items-center gap-2 mb-4">
                    <span
                      className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold"
                      style={{ backgroundColor: `${color}15`, color }}
                    >
                      {v.role}
                    </span>
                    {v.team && (
                      <span
                        className={`text-xs ${teamColor}`}
                        style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                      >
                        · {v.team}
                      </span>
                    )}
                  </div>
                  <p
                    className={`text-xs font-medium mb-3 ${roleColor}`}
                    style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                  >
                    {v.theme}
                  </p>
                  <i
                    className={`ri-double-quotes-l text-2xl mb-2 ${isDark ? 'text-white/20' : 'text-slate-300'}`}
                  />
                  <p
                    className={`text-sm leading-relaxed flex-1 ${cardText}`}
                    style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                  >
                    {v.quote}
                  </p>
                </article>
              </FadeIn>
            );
          })}
        </div>

        {showFootnote && (
          <FadeIn delay={120}>
            <p
              className={`text-xs mt-10 leading-relaxed ${isDark ? 'text-white/40' : 'text-[#94A3B8]'}`}
              style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
            >
              所有反馈均已脱敏，按职位角色标注，未透露公司名称、产品名称、技术栈细节。引用经客户内部确认可对外使用。
            </p>
          </FadeIn>
        )}
      </div>
    </section>
  );
}
